import { normalizeChainIdString } from "../../../../shared/caip2";
import { ChainStrategy } from "./chain-strategy";
/**
 * Manages protocol selection strategies for the staking module.
 * Provides a centralized way to select appropriate staking protocols based on different strategies.
 * Acts as the single source of truth for protocol information and provides context to strategies.
 *
 * @remarks
 * Relation to factory and maps:
 * The StakingModule initializes concrete protocol instances via the StakingProtocolFactory
 * and fills two maps: `stakingProtocolsById` and `stakingProtocolsByChain`. This manager
 * consumes those maps and exposes a strategy-driven selector (default: `ChainStrategy`).
 * Strategies query these maps using request context (e.g., `chainId`) to pick the relevant
 * protocol instance at runtime.
 */
export class ProtocolSelectionStrategyManager {
    logger;
    stakingProtocolsByChain;
    stakingProtocolsById;
    supportedStakingProtocols;
    strategy;
    constructor(options) {
        this.logger = options.logger;
        this.stakingProtocolsByChain = options.stakingProtocolsByChain;
        this.stakingProtocolsById = options.stakingProtocolsById;
        this.supportedStakingProtocols = options.supportedStakingProtocols;
        // Initialize with provided strategy or default chain strategy
        this.strategy = options.strategy ?? new ChainStrategy(this.logger);
    }
    /**
     * Gets all supported chains across all protocols.
     * Implementation of IProtocolSelectionContext interface.
     *
     * @returns Array of supported chain IDs
     */
    getSupportedChains() {
        return Array.from(this.stakingProtocolsByChain.keys());
    }
    /**
     * Selects the appropriate staking protocol using the current strategy.
     *
     * @param request - The staking module request
     * @returns The selected staking protocol instance
     * @throws {Error} When no suitable protocol is found
     */
    select(request) {
        this.logger.debug(`Selecting protocol for request with chainId: ${request.chainId}`);
        return this.strategy.selectProtocol(request, this);
    }
    /**
     * Sets the protocol selection strategy.
     *
     * @param strategy - The protocol selection strategy to use
     */
    setStrategy(strategy) {
        this.logger.debug(`Switching protocol selection strategy to: ${strategy.constructor.name}`);
        this.strategy = strategy;
    }
    /**
     * Gets the current protocol selection strategy.
     *
     * @returns The current protocol selection strategy
     */
    getStrategy() {
        return this.strategy;
    }
    /**
     * Gets the protocol instance for a specific chain.
     * Normalizes the chain ID to canonical CAIP-2 format before lookup.
     *
     * @param chainId - The chain identifier (e.g., "eip155:1", "solana:mainnet")
     * @returns The protocol instance or undefined if not supported
     */
    getProtocolsForChain(chainId) {
        const normalizedChainId = normalizeChainIdString(chainId);
        return this.stakingProtocolsByChain.get(normalizedChainId);
    }
    /**
     * Gets the protocol instance by its generated id.
     *
     * @param protocolId - The dynamically generated protocol id
     * @returns The protocol instance or undefined if not found
     */
    getProtocolById(protocolId) {
        return this.stakingProtocolsById.get(protocolId);
    }
    /**
     * Lists all supported protocols.
     *
     * @returns Array of supported protocol instances
     */
    getSupportedProtocols() {
        return Array.from(this.stakingProtocolsById.values());
    }
    /**
     * Gets the protocol definitions from the configuration.
     *
     * @returns Array of protocol definitions
     */
    getProtocolDefinitions() {
        return this.supportedStakingProtocols;
    }
}
//# sourceMappingURL=data:application/json;base64,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